/**
 * ListingPurchaseOptions
 *
 * Self-contained wrapper that:
 *  1. Loads lender rates from /api/lender-rates
 *  2. Reads the in-service date from the VIN check result already fired by MarketValueBadge
 *     (or fires its own lightweight fetch if needed)
 *  3. Renders PurchaseOptions with all listing data pre-filled
 */
import React, { useState, useEffect } from 'react';
import { PurchaseOptions } from '../../components';

const ListingPurchaseOptions = ({
  vin,
  price, // listing price in subunits (cents) from Sharetribe
  year,
  km,
  category = 'automotive',
  province,
  sellerType,
  listingId,
  historyScreen = null,
}) => {
  const [lenderRates, setLenderRates] = useState([]);
  const [inServiceDate, setInServiceDate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Convert Sharetribe subunit price (cents) to dollars
  const vehiclePrice = price ? Math.round(price / 100) : 24500;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        // Fire both requests in parallel
        const [ratesRes, vinRes] = await Promise.all([
          fetch('/api/lender-rates'),
          vin ? fetch(`/api/vin-check?vin=${encodeURIComponent(vin)}`) : Promise.resolve(null),
        ]);

        if (cancelled) return;

        const ratesJson = await ratesRes.json();
        if (!cancelled && ratesJson.lenders) {
          // Flatten into the array format findEligiblePrograms expects
          setLenderRates(ratesJson.lenders);
        }

        if (vinRes) {
          const vinJson = await vinRes.json();
          if (!cancelled && vinJson.inServiceDate) {
            setInServiceDate(vinJson.inServiceDate);
          }
        }
      } catch {
        // Non-fatal — calculator still renders with no lender match
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [vin]);

  if (loading) {
    return (
      <div style={{ padding: '20px 0', color: '#7A9CB0', fontSize: 13 }}>
        Loading purchase options…
      </div>
    );
  }

  return (
    <PurchaseOptions
      vehiclePrice={vehiclePrice}
      vehicleYear={year ? Number(year) : new Date().getFullYear() - 2}
      vehicleKm={km ? Number(km) : 50000}
      vehicleCategory={category}
      province={province}
      sellerType={sellerType}
      listingId={listingId}
      inServiceDate={inServiceDate}
      lenderRates={lenderRates}
      historyScreen={historyScreen}
    />
  );
};

export default ListingPurchaseOptions;
